'use client';

import { FC } from 'react';

import { Price } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItem } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

interface Props {
   shoppingCartItems: Array<ShoppingCartItem>;
}

export const CheckoutSuccessSummary: FC<Props> = ({ shoppingCartItems }) => {
   const translate = useTranslations();
   const format = useFormatter();

   const totalPrice: Price = {
      amount: shoppingCartItems.reduce((acc, cartItem) => acc + cartItem.unitPrice.price.amount * cartItem.quantity.amount, 0),
      currency: shoppingCartItems.length > 0 ? shoppingCartItems[0].unitPrice.price.currency : ''
   };

   return (
      <Paper>
         <Box direction="column" gap="sm">
            <Typography component="h3" variant="heading-4">
               {translate('shopping-cart.product-summary')}
            </Typography>
            <Divider />
            <Table stroked="horizontal">
               <TableBody>
                  {shoppingCartItems.map((shoppingCartItem) => {
                     const totalPricePerCartItem: Price = {
                        amount: shoppingCartItem.unitPrice.price.amount * shoppingCartItem.quantity.amount,
                        currency: shoppingCartItem.unitPrice.price.currency
                     };
                     return (
                        <TableRow key={shoppingCartItem.id}>
                           <TableCell>
                              <Typography variant="body-highlight">{shoppingCartItem.product.description}</Typography>
                           </TableCell>
                           <TableCell>
                              {translate('shopping-cart.quantity')}: {shoppingCartItem.quantity.amount} {shoppingCartItem.quantity.unitOfMeasure}
                              <br />
                              {translate('shopping-cart.packaging')}: {shoppingCartItem.packaging}
                              <br />
                              {translate('shopping-cart.unit-price')}: {format.unitPrice(shoppingCartItem.unitPrice)}
                              <br />
                              {translate('shopping-cart.total-price')}: {format.price(totalPricePerCartItem)}
                           </TableCell>
                        </TableRow>
                     );
                  })}
               </TableBody>
            </Table>
            <Divider />
            <Box items="baseline" justify="between">
               <Typography color="subtle"> {translate('shopping-cart.total-price')} </Typography>
               <Typography variant="heading-4"> {format.price(totalPrice)} </Typography>
            </Box>
         </Box>
      </Paper>
   );
};
